import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const AuthDialog = ({ open, onOpenChange }) => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Welcome back to the pass");
      } else {
        await register(email, password, name);
        toast.success("Right, you're in");
      }
      onOpenChange(false);
      setEmail(""); setPassword(""); setName("");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="auth-dialog"
        className="bg-stone-950 border border-stone-800 rounded-none max-w-md p-0"
      >
        <div className="p-8">
          <DialogHeader className="mb-6">
            <div className="eyebrow mb-3">The Regulars&apos; Table</div>
            <DialogTitle className="font-serif-display text-4xl text-stone-100 tracking-tight">
              {mode === "login" ? "Back again" : "Pull up a chair"}
            </DialogTitle>
            <p className="text-sm text-stone-400 mt-2">
              {mode === "login"
                ? "Sign in to save recipes in your cookbook."
                : "A quick account so we can keep your favourites safe."}
            </p>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-5">
            {mode === "register" && (
              <div>
                <label className="eyebrow block mb-2 text-stone-400">Name</label>
                <input
                  data-testid="auth-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  minLength={1}
                  className="w-full bg-stone-900 border border-stone-800 px-4 py-3 text-stone-100 focus:outline-none focus:border-orange-700 transition-colors"
                  placeholder="Your name"
                />
              </div>
            )}
            <div>
              <label className="eyebrow block mb-2 text-stone-400">Email</label>
              <input
                data-testid="auth-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full bg-stone-900 border border-stone-800 px-4 py-3 text-stone-100 focus:outline-none focus:border-orange-700 transition-colors"
                placeholder="you@somewhere.cook"
              />
            </div>
            <div>
              <label className="eyebrow block mb-2 text-stone-400">Password</label>
              <input
                data-testid="auth-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="w-full bg-stone-900 border border-stone-800 px-4 py-3 text-stone-100 focus:outline-none focus:border-orange-700 transition-colors"
                placeholder="At least 6 characters"
              />
            </div>

            <button
              data-testid="auth-submit"
              type="submit"
              disabled={busy}
              className="w-full py-4 bg-orange-700 hover:bg-orange-800 disabled:opacity-60 text-stone-50 text-xs uppercase tracking-[0.3em] font-medium transition-colors flex items-center justify-center gap-2"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {mode === "login" ? "Into the kitchen" : "Pull up a chair"}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-stone-500">
            {mode === "login" ? (
              <>
                No account?{" "}
                <button
                  data-testid="auth-toggle-register"
                  onClick={() => setMode("register")}
                  className="text-orange-500 hover:text-orange-400 ink-link"
                >
                  Create one
                </button>
              </>
            ) : (
              <>
                Already registered?{" "}
                <button
                  data-testid="auth-toggle-login"
                  onClick={() => setMode("login")}
                  className="text-orange-500 hover:text-orange-400 ink-link"
                >
                  Sign in
                </button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AuthDialog;
